import React, { useEffect, useRef } from "react";
import { useGetTestimonialsQuery } from "../redux/services/api";
import { useTranslation } from "../hooks/useTranslation";
import AOS from "aos";
import "aos/dist/aos.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Testimonials = () => {
  const swiperRef = useRef(null);

  // Use RTK Query to fetch testimonials
  const { data: testimonialsData, isLoading: loading } =
    useGetTestimonialsQuery();
  const testimonials = testimonialsData?.testimonials || [];
  const { t, isArabic } = useTranslation();

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isHalfStar = rating === i - 0.5;
      const isFullStar = rating >= i;

      stars.push(
        <span
          key={i}
          className="star-container"
          style={{
            position: "relative",
            display: "inline-block",
            marginRight: "2px",
          }}
        >
          {/* Background star (always gray) */}
          <span
            className="star"
            style={{
              color: "#666",
              position: "relative",
              zIndex: 1,
            }}
          >
            ★
          </span>

          {/* Foreground star (colored when selected) */}
          <span
            className={`star ${isFullStar || isHalfStar ? "filled" : ""}`}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 2,
              color: isFullStar || isHalfStar ? "#ffd700" : "transparent",
              overflow: isHalfStar ? "hidden" : "visible",
              width: isHalfStar ? "50%" : "100%",
            }}
          >
            ★
          </span>
        </span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title">{t("testimonials.title")}</h2>
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#ccc",
            }}
          >
            {t("common.loading")}
          </div>
        </div>
      </section>
    );
  }

  // Don't render the testimonials section if there are no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="testimonials">
      <div className="container">
        <h2 className="section-title" data-aos="fade-up" data-aos-delay="100">
          {t("testimonials.title")}
        </h2>
        <div className="testimonials-container">
          <Swiper
            key={isArabic ? "rtl" : "ltr"}
            modules={[Navigation, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={true}
            pagination={{
              enabled: testimonials.length > 1,
              clickable: true,
              dynamicBullets: false,
              type: "bullets",
            }}
            dir={isArabic ? "rtl" : "ltr"}
            breakpoints={{
              768: {
                slidesPerView: 1,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="testimonials-carousel"
          >
            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={testimonial.id}>
                <div
                  className="testimonial-card"
                  data-aos="zoom-in"
                  data-aos-delay={`${200 + index * 150}`}
                >
                  <div className="testimonial-avatar">
                    {testimonial.image_url ? (
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : null}
                  </div>
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.content}</p>
                  <div className="stars">{renderStars(testimonial.rating)}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
